export const EVENTS_ROUTE = '/events';
export const EVENTS_ADMIN_ROUTE = '/events-admin';

export const EVENT_ACTIONS = Object.freeze({
  GET_EVENTS: 'getEvents',
  GET_EVENT: 'getEvent',
  RSVP: 'rsvpToEvent',
  CANCEL_RSVP: 'cancelRsvp',
  GET_RSVP: 'getRsvp',
  CREATE_EVENT: 'createEvent',
  UPDATE_EVENT: 'updateEvent',
  CANCEL_EVENT: 'cancelEvent',
  REMOVE_ATTENDEE: 'removeAttendee',
  GET_ATTENDEES: 'getEventAttendees',
});

export const EVENT_SCOPES = Object.freeze({
  UPCOMING: 'upcoming',
  PAST: 'past',
  ALL: 'all',
});

export const EVENT_STATUSES = Object.freeze({
  OPEN: 'open',
  CLOSED: 'closed',
  CANCELLED: 'cancelled',
});

export const RSVP_STATUSES = Object.freeze({
  GOING: 'going',
  MAYBE: 'maybe',
  NOT_GOING: 'not_going',
  WAITLISTED: 'waitlisted',
  CANCELLED: 'cancelled',
});

export const RSVP_CHOICES = Object.freeze([
  { status: RSVP_STATUSES.GOING, label: "I'm going" },
  { status: RSVP_STATUSES.MAYBE, label: 'Maybe' },
  { status: RSVP_STATUSES.NOT_GOING, label: "Can't make it" },
]);

export const RSVP_STATUS_LABELS = Object.freeze({
  [RSVP_STATUSES.GOING]: 'Going',
  [RSVP_STATUSES.MAYBE]: 'Maybe',
  [RSVP_STATUSES.NOT_GOING]: 'Not going',
  [RSVP_STATUSES.WAITLISTED]: 'Waitlisted',
  [RSVP_STATUSES.CANCELLED]: 'Cancelled',
});

export const EVENT_AVAILABILITY = Object.freeze({
  OPEN: 'open',
  FULL_WAITLIST: 'full_waitlist',
  FULL: 'full',
  CLOSED: 'closed',
  CANCELLED: 'cancelled',
  STARTED: 'started',
});

export const AVAILABILITY_LABELS = Object.freeze({
  [EVENT_AVAILABILITY.OPEN]: 'Open',
  [EVENT_AVAILABILITY.FULL_WAITLIST]: 'Full - waitlist open',
  [EVENT_AVAILABILITY.FULL]: 'Full',
  [EVENT_AVAILABILITY.CLOSED]: 'RSVPs closed',
  [EVENT_AVAILABILITY.CANCELLED]: 'Cancelled',
  [EVENT_AVAILABILITY.STARTED]: 'Started',
});

export const EVENT_ERROR_CODES = Object.freeze({
  EVENT_NOT_FOUND: 'EVENT_NOT_FOUND',
  EVENT_CLOSED: 'EVENT_CLOSED',
  EVENT_CANCELLED: 'EVENT_CANCELLED',
  EVENT_STARTED: 'EVENT_STARTED',
  EVENT_FULL: 'EVENT_FULL',
  GUESTS_NOT_ALLOWED: 'GUESTS_NOT_ALLOWED',
  RSVP_NOT_FOUND: 'RSVP_NOT_FOUND',
  INVALID_CANCEL_TOKEN: 'INVALID_CANCEL_TOKEN',
  IDENTITY_UNRESOLVED: 'IDENTITY_UNRESOLVED',
  CAPACITY_BELOW_GOING: 'CAPACITY_BELOW_GOING',
});

export const EVENT_ERROR_MESSAGES = Object.freeze({
  [EVENT_ERROR_CODES.EVENT_NOT_FOUND]: 'We could not find that event. It may have been removed.',
  [EVENT_ERROR_CODES.EVENT_CLOSED]: 'RSVPs for this event are closed.',
  [EVENT_ERROR_CODES.EVENT_CANCELLED]: 'This event has been cancelled.',
  [EVENT_ERROR_CODES.EVENT_STARTED]:
    'This event has already started, so RSVPs can no longer change.',
  [EVENT_ERROR_CODES.EVENT_FULL]: 'This event is full and has no waitlist.',
  [EVENT_ERROR_CODES.GUESTS_NOT_ALLOWED]:
    'This event is for members only. Log in to RSVP with your Black Box VR account.',
  [EVENT_ERROR_CODES.RSVP_NOT_FOUND]: 'We could not find your RSVP for this event.',
  [EVENT_ERROR_CODES.INVALID_CANCEL_TOKEN]:
    'We could not verify this RSVP from this browser. Please contact the gym to change it.',
  [EVENT_ERROR_CODES.IDENTITY_UNRESOLVED]:
    'We could not tell who you are. Log in, or enter your name and an email or phone number.',
  [EVENT_ERROR_CODES.CAPACITY_BELOW_GOING]:
    'Capacity cannot be lower than the number of people already going.',
});

export const EVENT_COPY = Object.freeze({
  PAGE_TITLE: 'Black Box VR - Events',
  PAGE_DESCRIPTION: 'Find Black Box VR community events and RSVP in seconds.',
  LIST_TITLE: 'Events',
  LIST_SUBTITLE: 'Join the community. Reserve your spot.',
  EMPTY_UPCOMING: 'No upcoming events right now. Check back soon.',
  EMPTY_PAST: 'No past events.',
  LOAD_ERROR: 'We could not load events. Please try again.',
  DETAIL_LOAD_ERROR: 'We could not load this event. Please try again.',
  GENERIC_ERROR: 'Something went wrong. Please try again.',
  BACK_TO_EVENTS: 'All events',
  ADD_TO_CALENDAR: 'Add to calendar',
  RSVP_SAVED: 'Your RSVP is saved.',
  RSVP_WAITLISTED:
    "The event is full, so you're on the waitlist. We'll let you know if a spot opens.",
  RSVP_CANCELLED: 'Your RSVP was cancelled.',
  GUEST_NAME_REQUIRED: 'Please enter your name.',
  GUEST_CONTACT_REQUIRED: 'Please enter an email or phone number.',
  GUEST_EMAIL_INVALID: 'Please enter a valid email address.',
  GUEST_PHONE_INVALID: 'Please enter a valid phone number.',
  GUEST_TOKEN_NOTE:
    'Keep this code if you need to change your RSVP from another device. This browser remembers it for you.',
});

export const ADMIN_COPY = Object.freeze({
  TITLE: 'Events admin',
  STAFF_NAME_LABEL: 'Your staff name',
  STAFF_NAME_REQUIRED: 'Enter your staff name first.',
  TITLE_REQUIRED: 'Title is required.',
  START_REQUIRED: 'Start date and time are required.',
  END_INVALID: 'End time must be after the start time.',
  CAPACITY_INVALID: 'Capacity must be a whole number of 1 or more, or blank for unlimited.',
  TITLE_TOO_LONG: 'Title is too long.',
  DESCRIPTION_TOO_LONG: 'Description is too long.',
  LOCATION_TOO_LONG: 'Location is too long.',
  EVENT_CREATED: 'Event created.',
  EVENT_UPDATED: 'Event updated.',
  EVENT_CANCELLED: 'Event cancelled and attendees notified.',
  ATTENDEE_REMOVED: 'Attendee removed.',
  CANCEL_CONFIRM_TITLE: 'Cancel this event?',
  CANCEL_CONFIRM_BODY:
    'Everyone who RSVPed will be marked cancelled and notified. This cannot be undone.',
  REMOVE_CONFIRM_TITLE: 'Remove this attendee?',
  REMOVE_CONFIRM_BODY: 'Their RSVP will be cancelled and they will be notified.',
});

export const EVENT_LIMITS = Object.freeze({
  TITLE: 150,
  DESCRIPTION: 4000,
  LOCATION: 200,
  LIST_LIMIT: 100,
  ATTENDEES_PAGE: 200,
});

export const STAFF_NAME_STORAGE_KEY = 'bbvr.events.staffName';
export const GUEST_RSVP_STORAGE_PREFIX = 'bbvr.rsvp.';

export const DATE_TIME_LOCAL_FORMAT = 'YYYY-MM-DDTHH:mm';
export const EVENT_DATE_FORMAT = 'ddd, MMM D, YYYY';
export const EVENT_TIME_FORMAT = 'h:mm A';
export const DEFAULT_EVENT_DURATION_MS = 60 * 60 * 1000;

export const EVENT_COLORS = Object.freeze({
  panel: '#1A1D20',
  row: '#22262A',
  border: 'rgba(255,255,255,0.08)',
  muted: '#8A9096',
  accent: '#0076FF',
  warn: '#E8C547',
  danger: '#E85D2A',
  good: '#4CAF50',
});

export const CSV_HEADERS = Object.freeze([
  'Name',
  'Type',
  'Status',
  'Waitlist position',
  'Email',
  'Phone',
  'RSVP date',
]);

export const ICS_PRODID = '-//Black Box VR//Events//EN';
export const ICS_UID_DOMAIN = 'blackbox-vr.com';
